import React, { useState } from "react";
import { NextPage } from "next";
import styled, { useTheme } from "styled-components";
import {
  ContentContainer,
  StyledButton,
  StyledCard,
  StyledInput,
  StyledScreen,
  StyledCta,
} from "../components/styles";
import { useFormSubmissionData } from "../hooks/useFormSubmissionData";
import { useRouter } from "next/router";
import { Routes } from "../routes";
import ErrorIcon from "@mui/icons-material/Error";
import { Oval } from "react-loader-spinner";
import {
  BUTTON_HIGHLIGHT_HEX_OPACITY_SUFFIX,
  BACKGROUND_IMAGE_OPACITY,
  HAPE_BACKGROUND_IMAGE_URL,
  MOBILE_BREAKPOINT,
  IS_FORM_CLOSED,
} from "../constants";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { ScreenLoader } from "../components/screenLoader";
import { useIsMounted } from "../hooks/useIsMounted";
import { StyledInitialRenderScreen } from "../components/styles/StyledInitialRenderScreen";
import { FormErrorView } from "../components/form_error";

const ImageScreen = styled(StyledScreen)`
  background: ${`url("${HAPE_BACKGROUND_IMAGE_URL}") no-repeat center center / cover}`};
  background-color: ${(props) => `${props.theme.colors.container.primary}`};
`;

const ScreenGradient = styled(StyledScreen)`
  background-color: ${(props) =>
    `${props.theme.colors.container.primary}${BACKGROUND_IMAGE_OPACITY}`};
`;

const Title = styled.h1`
  color: ${(props) => props.theme.colors.text.primary};
  font-size: ${(props) => props.theme.fontSize.title};
  font-weight: bold;
  margin: 0px 0px;
`;

const Description = styled.h3`
  color: ${(props) => props.theme.colors.text.primary};
  font-size: ${(props) => props.theme.fontSize.small};
  font-weight: 400;
  margin: 4px 0px;
  margin-top: 12px;
`;

const CTA = styled(StyledCta)`
  margin-top: 0px;
  margin-bottom: 24px;
  align-self: center;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
`;

const Logo = styled.img`
  width: 130px;
  height: 130px;
  border-radius: 8px;
  object-fit: cover;
  margin-left: 12px;
`;

const TitleRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const QuestionTitleContainer = styled.div`
  display: flex;
  flex-direction: row;
`;
const QuestionTitle = styled.h4`
  color: ${(props) => props.theme.colors.text.primary};
  margin: 6px 0px;
  font-weight: 400;
  padding: 0px 0px;
`;

const Required = styled(QuestionTitle)`
  color: ${(props) => props.theme.colors.action.primary};
  margin-left: 2px;
`;

const TitleCard = styled(StyledCard)`
  margin-bottom: 12px;
  width: 100%;
`;

const AnswerCard = styled(StyledCard)`
  margin-bottom: 12px;
  width: 100%;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: 6px 0px;
`;

const ErrorMessage = styled.h5`
  font-size: ${(props) => props.theme.fontSize.small};
  font-weight: 400;
  color: ${(props) => props.theme.colors.text.error};
  margin: 0px;
  margin-top: 12px;
  margin-left: 4px;
  overflow-wrap: break-word;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    overflow-wrap: anywhere;
  }
`;

const SubmissionErrorMessage = styled(ErrorMessage)`
  margin: 0px;
  margin-left: 8px;
`;

const SuccessMessage = styled(QuestionTitle)`
  margin-left: 6px;
  color: ${(props) => props.theme.colors.text.primary};
  font-size: ${(props) => props.theme.fontSize.small};
  overflow-wrap: anywhere;
`;

const Form: NextPage = () => {
  const router = useRouter();
  const theme = useTheme();
  const {
    data,
    updateAnswer,
    validate,
    submitForm,
    submissionError,
    isLoading,
    walletAddress,
    isFetchingData,
  } = useFormSubmissionData();
  const [formSubmitted, setFormSubmitted] = useState(false);
  const { isMounted } = useIsMounted();

  const onSubmissionClick = async () => {
    if (isLoading) return;
    const isValid = validate();
    if (!isValid) return;

    const submissionResult = await submitForm();

    if (!submissionResult.success) return;
    setFormSubmitted(true);
  };

  const onAnswerTextChange = (text: string, index: number) => {
    updateAnswer(text, index);
  };

  const onCTAClick = () => {
    router.push(Routes.Landing);
  };

  const renderSubmitRow = () => {
    if (formSubmitted) {
      return (
        <Column>
          <Row>
            <CheckCircleIcon
              fontSize="medium"
              sx={{ color: theme.colors.text.success }}
            />
            <SuccessMessage>{`Form submitted by wallet address: ${walletAddress}`}</SuccessMessage>
          </Row>
          <SuccessMessage>You can now close this window</SuccessMessage>
        </Column>
      );
    }
    return (
      <Row>
        <StyledButton isDisabled={isLoading} onClick={onSubmissionClick}>
          Submit
        </StyledButton>
        {isLoading && (
          <Oval
            height={30}
            width={30}
            wrapperStyle={{ marginLeft: "8px" }}
            color={theme.colors.action.primary}
            visible={true}
            ariaLabel="oval-loading"
            secondaryColor={`${theme.colors.action.primary}${BUTTON_HIGHLIGHT_HEX_OPACITY_SUFFIX}`}
            strokeWidth={4}
            strokeWidthSecondary={4}
          />
        )}
      </Row>
    );
  };

  if (!isMounted) {
    return <StyledInitialRenderScreen />;
  }

  if (isFetchingData) {
    return <ScreenLoader />;
  }

  if (!data) {
    return <FormErrorView />;
  }

  const renderHeaderRow = () => (
    <>
      <CTA onClick={onCTAClick}>Powered by Crypto Forms</CTA>
      <TitleCard>
        <TitleRow>
          <Column>
            <Title>{data.title}</Title>
            {data.description && <Description>{data.description}</Description>}
          </Column>
          {data.logoUrl && <Logo src={data.logoUrl} />}
        </TitleRow>
      </TitleCard>
    </>
  );

  //TODO: Clean this up and change env variable to response on form api call
  if (IS_FORM_CLOSED) {
    return (
      <ImageScreen>
        <ScreenGradient>
          <ContentContainer>
            {renderHeaderRow()}
            <TitleCard>
              <QuestionTitle>Form Closed</QuestionTitle>
            </TitleCard>
          </ContentContainer>
        </ScreenGradient>
      </ImageScreen>
    );
  }

  return (
    <ImageScreen>
      <ScreenGradient>
        <ContentContainer>
          {renderHeaderRow()}
          {data.questions.map((question, index) => {
            const onTextChange = (event: any) => {
              onAnswerTextChange(event.currentTarget.value, index);
            };

            return (
              <AnswerCard key={question.id}>
                <QuestionTitleContainer>
                  <QuestionTitle>{question.body}</QuestionTitle>
                  {question.isRequired && <Required>*</Required>}
                </QuestionTitleContainer>
                <StyledInput
                  disabled={isLoading || formSubmitted}
                  placeholder="e.g 0x11r323E29s98a8654qq0D09Dw21bra42cB1lpoDw"
                  value={question.answer}
                  onChange={onTextChange}
                />
                {question.hasError && (
                  <ErrorMessage>This field is required</ErrorMessage>
                )}
              </AnswerCard>
            );
          })}
          {renderSubmitRow()}
          {submissionError && (
            <Row>
              <ErrorIcon
                fontSize="small"
                sx={{ color: theme.colors.text.error }}
              />
              <SubmissionErrorMessage>{submissionError}</SubmissionErrorMessage>
            </Row>
          )}
        </ContentContainer>
      </ScreenGradient>
    </ImageScreen>
  );
};

export default Form;
